import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { countries } from '../data/countries';
import './OfferForm.css';

interface OfferFormData {
  name: string;
  advertiserId: string;
  goalType: 'install' | 'lead' | 'sale' | 'event';
  eventName: string;
  category: 'Incent' | 'Non Incent';
  payout: string;
  payoutCurrency: string;
  dailyClickCap: string;
  monthlyClickCap: string;
  dailyConversionCap: string;
  monthlyConversionCap: string;
  redirectUrlTemplate: string;
  previewUrl: string;
  description: string;
  restriction: string;
  isActive: boolean;
  isTest: boolean;
  countries: string[];
  deviceTypes: string[];
  startDate: string;
  endDate: string;
}

interface OfferEventFormData {
  eventName: string;
  payout: string;
  revenue: string;
  payoutCurrency: string;
  dailyConversionCap: string;
  monthlyConversionCap: string;
  isActive: boolean;
}

const EditOffer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<OfferFormData>({
    name: '',
    advertiserId: '',
    goalType: 'install',
    eventName: '',
    category: 'Non Incent',
    payout: '',
    payoutCurrency: 'USD',
    dailyClickCap: '',
    monthlyClickCap: '',
    dailyConversionCap: '',
    monthlyConversionCap: '',
    redirectUrlTemplate: '',
    previewUrl: '',
    description: '',
    restriction: '',
    isActive: true,
    isTest: false,
    countries: [],
    deviceTypes: [],
    startDate: '',
    endDate: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [advertisers, setAdvertisers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isActiveTouched, setIsActiveTouched] = useState(false);
  const [eventConfigs, setEventConfigs] = useState<OfferEventFormData[]>([]);

  // Publisher assignment state
  const [assignments, setAssignments] = useState<any[]>([]);
  const [allPublishers, setAllPublishers] = useState<any[]>([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedPublisherIds, setSelectedPublisherIds] = useState<number[]>([]);
  const [publisherSearch, setPublisherSearch] = useState('');
  const [showPublisherDropdown, setShowPublisherDropdown] = useState(false);
  const [customPayout, setCustomPayout] = useState<string>('');
  const [assignmentNotes, setAssignmentNotes] = useState<string>('');
  const [pubDailyClickCap, setPubDailyClickCap] = useState<string>('');
  const [pubMonthlyClickCap, setPubMonthlyClickCap] = useState<string>('');
  const [pubDailyConversionCap, setPubDailyConversionCap] = useState<string>('');
  const [pubMonthlyConversionCap, setPubMonthlyConversionCap] = useState<string>('');
  const publisherDropdownRef = useRef<HTMLDivElement>(null);

  // Multi-select country dropdown state
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [filteredCountries, setFilteredCountries] = useState<string[]>(countries);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const countryInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch advertisers, offer data, publishers, and assignments in parallel
        const [advertisersResponse, offerResponse, publishersResponse, assignmentsResponse] = await Promise.all([
          apiService.getAdvertisers(),
          id ? apiService.getOffer(parseInt(id)) : Promise.resolve(null),
          apiService.getPublishers(),
          id ? apiService.getOfferPublishers(parseInt(id)) : Promise.resolve({ assignments: [] })
        ]);

        setAdvertisers(advertisersResponse.advertisers || []);
        setAllPublishers(publishersResponse.publishers || []);
        setAssignments(assignmentsResponse.assignments || []);

        if (offerResponse?.offer) {
          const offer = offerResponse.offer;

          // Format dates for input fields (YYYY-MM-DD)
          const formatDate = (dateStr: string | null) => {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            return date.toISOString().split('T')[0];
          };

          // Determine if goal_type is a custom event
          const standardGoalTypes = ['install', 'lead', 'sale'];
          const isCustomEvent = offer.goal_type && !standardGoalTypes.includes(offer.goal_type);

          setFormData({
            name: offer.name || '',
            advertiserId: offer.advertiser_id?.toString() || '',
            goalType: isCustomEvent ? 'event' : (offer.goal_type || 'install'),
            eventName: isCustomEvent ? offer.goal_type : (offer.event_name || ''),
            category: offer.category || 'Non Incent',
            payout: offer.payout?.toString() || '',
            payoutCurrency: offer.payout_currency || 'USD',
            dailyClickCap: offer.daily_click_cap?.toString() || '',
            monthlyClickCap: offer.monthly_click_cap?.toString() || '',
            dailyConversionCap: offer.daily_conversion_cap?.toString() || '',
            monthlyConversionCap: offer.monthly_conversion_cap?.toString() || '',
            redirectUrlTemplate: offer.redirect_url_template || '',
            previewUrl: offer.preview_url || '',
            description: offer.description || '',
            restriction: offer.restriction || '',
            isActive: offer.is_active ?? true,
            isTest: offer.is_test ?? false,
            countries: offer.countries || [],
            deviceTypes: offer.os || [],
            startDate: formatDate(offer.start_date),
            endDate: formatDate(offer.end_date)
          });
          const offerEvents = Array.isArray(offer.events) ? offer.events : [];
          setEventConfigs(offerEvents
            .filter((eventConfig: any) => !eventConfig.is_primary)
            .map((eventConfig: any) => ({
              eventName: eventConfig.event_name || '',
              payout: '',
              revenue: '',
              payoutCurrency: eventConfig.payout_currency || offer.payout_currency || 'USD',
              dailyConversionCap: eventConfig.daily_conversion_cap?.toString() || '',
              monthlyConversionCap: eventConfig.monthly_conversion_cap?.toString() || '',
              isActive: eventConfig.is_active ?? true
            })));
          setIsActiveTouched(false);
        }
      } catch (error: any) {
        console.error('Failed to fetch data:', error);
        setErrors({ ...errors, fetch: error.message || 'Failed to load data' });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Close country dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target as Node) &&
        countryInputRef.current &&
        !countryInputRef.current.contains(event.target as Node)
      ) {
        setShowCountryDropdown(false);
      }
      // Close publisher dropdown when clicking outside
      const target = event.target as HTMLElement;
      if (showPublisherDropdown && !target.closest('[data-publisher-dropdown]')) {
        setShowPublisherDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPublisherDropdown]);

  const availableDeviceTypes = ['iPhone', 'iPad', 'Android Phone', 'Android Tablet', 'Desktop', 'Mobile Web'];

  const getPrimaryEventName = () => (
    formData.goalType === 'event' ? formData.eventName.trim() : formData.goalType
  );

  const buildEventsPayload = () => {
    const primaryEvent = {
      event_name: getPrimaryEventName() || 'install',
      event_order: 1,
      is_primary: true,
      payout: formData.payout ? parseFloat(formData.payout) : null,
      revenue: formData.payout ? parseFloat(formData.payout) : null,
      payout_currency: formData.payoutCurrency,
      daily_conversion_cap: formData.dailyConversionCap ? parseInt(formData.dailyConversionCap) : null,
      monthly_conversion_cap: formData.monthlyConversionCap ? parseInt(formData.monthlyConversionCap) : null,
      publisher_postback_enabled: true,
      publisher_postback_percentage: null,
      postback_status: 'start',
      require_prior_event: false,
      is_payable: true,
      is_active: true
    };

    return [
      primaryEvent,
      ...eventConfigs
        .filter(eventConfig => eventConfig.eventName.trim())
        .map((eventConfig, index) => ({
          event_name: eventConfig.eventName.trim(),
          event_order: index + 2,
          is_primary: false,
          payout: null,
          revenue: null,
          payout_currency: eventConfig.payoutCurrency || formData.payoutCurrency,
          daily_conversion_cap: eventConfig.dailyConversionCap ? parseInt(eventConfig.dailyConversionCap) : null,
          monthly_conversion_cap: eventConfig.monthlyConversionCap ? parseInt(eventConfig.monthlyConversionCap) : null,
          publisher_postback_enabled: false,
          publisher_postback_percentage: null,
          postback_status: 'pause',
          require_prior_event: false,
          is_payable: false,
          is_active: eventConfig.isActive
        }))
    ];
  };

  const addEventConfig = () => {
    setEventConfigs(prev => ([
      ...prev,
      {
        eventName: '',
        payout: '',
        revenue: '',
        payoutCurrency: formData.payoutCurrency,
        dailyConversionCap: '',
        monthlyConversionCap: '',
        isActive: true
      }
    ]));
  };

  const updateEventConfig = (index: number, field: keyof OfferEventFormData, value: string | boolean) => {
    setEventConfigs(prev => prev.map((eventConfig, eventIndex) => (
      eventIndex === index ? { ...eventConfig, [field]: value } : eventConfig
    )));
  };

  const removeEventConfig = (index: number) => {
    setEventConfigs(prev => prev.filter((_, eventIndex) => eventIndex !== index));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      if (name === 'isActive') {
        setIsActiveTouched(true);
      }
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCountrySearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCountrySearch(value);
    setShowCountryDropdown(true);

    // Filter countries based on search term
    const filtered = countries.filter(country =>
      country.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredCountries(filtered);
  };

  const handleCountryToggle = (country: string) => {
    setFormData(prev => ({
      ...prev,
      countries: prev.countries.includes(country)
        ? prev.countries.filter(c => c !== country)
        : [...prev.countries, country]
    }));
  };

  const handleRemoveCountry = (country: string) => {
    setFormData(prev => ({
      ...prev,
      countries: prev.countries.filter(c => c !== country)
    }));
  };

  const handleSelectAllCountries = () => {
    setFormData(prev => ({
      ...prev,
      countries: [...countries]
    }));
  };

  const handleDeselectAllCountries = () => {
    setFormData(prev => ({
      ...prev,
      countries: []
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Offer name is required';
    }

    if (!formData.advertiserId) {
      newErrors.advertiserId = 'Advertiser is required';
    }

    if (!formData.payout.trim()) {
      newErrors.payout = 'Payout is required';
    } else if (isNaN(Number(formData.payout)) || Number(formData.payout) <= 0) {
      newErrors.payout = 'Payout must be a positive number';
    }

    if (!formData.redirectUrlTemplate.trim()) {
      newErrors.redirectUrlTemplate = 'Redirect URL is required';
    } else if (!/^https?:\/\/.+/.test(formData.redirectUrlTemplate)) {
      newErrors.redirectUrlTemplate = 'Redirect URL must be a valid URL';
    }

    if (formData.goalType === 'event' && !formData.eventName.trim()) {
      newErrors.eventName = 'Event name is required for custom events';
    }

    const eventNames = new Set<string>();
    const primaryEventName = getPrimaryEventName();
    if (primaryEventName) eventNames.add(primaryEventName);
    eventConfigs.forEach((eventConfig, index) => {
      const eventName = eventConfig.eventName.trim();
      if (!eventName) {
        newErrors[`event_${index}`] = 'Event name is required';
      } else if (eventNames.has(eventName)) {
        newErrors[`event_${index}`] = 'Event name must be unique';
      }
      eventNames.add(eventName);

    });

    if (formData.countries.length === 0) {
      newErrors.countries = 'At least one country must be selected';
    }

    if (formData.deviceTypes.length === 0) {
      newErrors.deviceTypes = 'At least one device type must be selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id) return;

    if (validateForm()) {
      try {
        setLoading(true);
        const updateData: any = {
          name: formData.name,
          advertiser_id: parseInt(formData.advertiserId),
          goal_type: formData.goalType === 'event' ? formData.eventName : formData.goalType,
          category: formData.category,
          payout: parseFloat(formData.payout),
          payout_currency: formData.payoutCurrency,
          daily_click_cap: formData.dailyClickCap ? parseInt(formData.dailyClickCap) : null,
          monthly_click_cap: formData.monthlyClickCap ? parseInt(formData.monthlyClickCap) : null,
          daily_conversion_cap: formData.dailyConversionCap ? parseInt(formData.dailyConversionCap) : null,
          monthly_conversion_cap: formData.monthlyConversionCap ? parseInt(formData.monthlyConversionCap) : null,
          redirect_url_template: formData.redirectUrlTemplate,
          preview_url: formData.previewUrl || null,
          description: formData.description || null,
          restriction: formData.restriction || null,
          is_test: formData.isTest,
          start_date: formData.startDate ? new Date(formData.startDate).toISOString() : null,
          end_date: formData.endDate ? new Date(formData.endDate).toISOString() : null,
          countries: formData.countries,
          os: formData.deviceTypes,
          events: buildEventsPayload()
        };

        if (isActiveTouched) {
          updateData.is_active = formData.isActive;
        }

        await apiService.updateOffer(parseInt(id), updateData);
        alert('Offer updated successfully!');
        navigate('/offers');
      } catch (error: any) {
        console.error('Failed to update offer:', error);
        setErrors({ ...errors, submit: error.message || 'Failed to update offer' });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancel = () => {
    navigate('/offers');
  };

  // Publisher assignment handlers
  const handleAssignPublisher = async () => {
    if (!id || selectedPublisherIds.length === 0) return;

    try {
      const assignmentPromises = selectedPublisherIds.map(publisherId =>
        apiService.assignPublisherToOffer(
          parseInt(id),
          publisherId,
          {
            status: 'approved',
            custom_payout: customPayout || undefined,
            notes: assignmentNotes || undefined,
            daily_click_cap: pubDailyClickCap ? parseInt(pubDailyClickCap) : undefined,
            monthly_click_cap: pubMonthlyClickCap ? parseInt(pubMonthlyClickCap) : undefined,
            daily_conversion_cap: pubDailyConversionCap ? parseInt(pubDailyConversionCap) : undefined,
            monthly_conversion_cap: pubMonthlyConversionCap ? parseInt(pubMonthlyConversionCap) : undefined
          }
        )
      );

      await Promise.all(assignmentPromises);

      // Refresh assignments
      const assignmentsResponse = await apiService.getOfferPublishers(parseInt(id));
      setAssignments(assignmentsResponse.assignments || []);

      // Reset form
      setShowAssignModal(false);
      setSelectedPublisherIds([]);
      setPublisherSearch('');
      setCustomPayout('');
      setAssignmentNotes('');
      setPubDailyClickCap('');
      setPubMonthlyClickCap('');
      setPubDailyConversionCap('');
      setPubMonthlyConversionCap('');
      alert(`Successfully assigned ${selectedPublisherIds.length} publisher(s)`);
    } catch (err: any) {
      alert(err.message || 'Failed to assign publisher(s)');
    }
  };

  const handleRemovePublisher = async (publisherId: number) => {
    if (!id || !window.confirm('Are you sure you want to remove this publisher from the offer?')) return;

    try {
      await apiService.removePublisherFromOffer(parseInt(id), publisherId);

      // Refresh assignments
      const assignmentsResponse = await apiService.getOfferPublishers(parseInt(id));
      setAssignments(assignmentsResponse.assignments || []);
    } catch (err: any) {
      alert(err.message || 'Failed to remove publisher');
    }
  };

  const handlePublisherToggle = (publisherId: number) => {
    setSelectedPublisherIds(prev =>
      prev.includes(publisherId)
        ? prev.filter(id => id !== publisherId)
        : [...prev, publisherId]
    );
  };

  const handleRemoveSelectedPublisher = (publisherId: number) => {
    setSelectedPublisherIds(prev => prev.filter(pid => pid !== publisherId));
  };

  const handleSelectAllPublishers = () => {
    const availableIds = getAvailablePublishers()
      .filter(pub =>
        pub.name.toLowerCase().includes(publisherSearch.toLowerCase()) ||
        pub.email.toLowerCase().includes(publisherSearch.toLowerCase())
      )
      .map(pub => pub.id);
    setSelectedPublisherIds(availableIds);
  };

  const handleDeselectAllPublishers = () => {
    setSelectedPublisherIds([]);
  };

  const getAvailablePublishers = () => {
    const assignedPublisherIds = new Set(assignments.map(a => a.publisher.id));
    return allPublishers.filter(p => !assignedPublisherIds.has(p.id));
  };

  // Calculate tier-based payouts for display
  const calculateTierPayouts = () => {
    const payout = parseFloat(formData.payout) || 0;
    return {
      standard: (payout * 0.40).toFixed(2),   // 40% for standard tier
      premium: (payout * 0.60).toFixed(2),    // 60% for premium tier
      sensitive: (payout * 0.80).toFixed(2)   // 80% for sensitive tier
    };
  };

  const tierPayouts = calculateTierPayouts();

  return (
    <div className="offer-form-page">
      <div className="page-header">
        <h1>Edit Offer - ID: {id}</h1>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="offer-form">
          <div className="form-section">
            <h2>Basic Information</h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Offer Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={errors.name ? 'error' : ''}
                  placeholder="Enter offer name"
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="advertiserId">Advertiser *</label>
                <select
                  id="advertiserId"
                  name="advertiserId"
                  value={formData.advertiserId}
                  onChange={handleInputChange}
                  className={errors.advertiserId ? 'error' : ''}
                >
                  <option value="">Select Advertiser</option>
                  {advertisers.map(advertiser => (
                    <option key={advertiser.id} value={advertiser.id}>
                      {advertiser.name}
                    </option>
                  ))}
                </select>
                {errors.advertiserId && <span className="error-message">{errors.advertiserId}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="goalType">Goal Type *</label>
                <select
                  id="goalType"
                  name="goalType"
                  value={formData.goalType}
                  onChange={handleInputChange}
                >
                  <option value="install">App Install</option>
                  <option value="lead">Lead Generation</option>
                  <option value="sale">Sale/Purchase</option>
                  <option value="event">Custom Event</option>
                </select>
              </div>

              {formData.goalType === 'event' && (
                <div className="form-group">
                  <label htmlFor="eventName">Event Name *</label>
                  <input
                    type="text"
                    id="eventName"
                    name="eventName"
                    value={formData.eventName}
                    onChange={handleInputChange}
                    placeholder="e.g., purchase, registration, subscription"
                    className={errors.eventName ? 'error' : ''}
                  />
                  {errors.eventName && <span className="error-message">{errors.eventName}</span>}
                  <small className="field-hint">Enter the custom event name for tracking</small>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="category">Category</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                >
                  <option value="Non Incent">Non Incent</option>
                  <option value="Incent">Incent</option>
                </select>
                <small className="field-hint">Offer incentive category</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="payout">Payout *</label>
                <div className="payout-group">
                  <input
                    type="number"
                    id="payout"
                    name="payout"
                    value={formData.payout}
                    onChange={handleInputChange}
                    className={errors.payout ? 'error' : ''}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                  <select
                    name="payoutCurrency"
                    value={formData.payoutCurrency}
                    onChange={handleInputChange}
                    className="currency-select"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="CAD">CAD</option>
                  </select>
                </div>
                {errors.payout && <span className="error-message">{errors.payout}</span>}
              </div>
            </div>

            {/* Tier-based Payout Display */}
            {formData.payout && parseFloat(formData.payout) > 0 && (
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Publisher Payouts by Tier</label>
                  <div className="tier-payouts-display">
                    <div className="tier-payout-item">
                      <span className="tier-label">Standard Tier (40%):</span>
                      <span className="tier-amount">{formData.payoutCurrency} {tierPayouts.standard}</span>
                    </div>
                    <div className="tier-payout-item">
                      <span className="tier-label">Premium Tier (60%):</span>
                      <span className="tier-amount">{formData.payoutCurrency} {tierPayouts.premium}</span>
                    </div>
                    <div className="tier-payout-item">
                      <span className="tier-label">Sensitive Tier (80%):</span>
                      <span className="tier-amount">{formData.payoutCurrency} {tierPayouts.sensitive}</span>
                    </div>
                  </div>
                  <small className="field-hint">
                    Publishers receive different payouts based on their tier level
                  </small>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group full-width">
                <div className="event-config-header">
                  <div>
                    <label>Additional Events</label>
                    <small className="field-hint">
                      Primary payable event is {getPrimaryEventName() || 'install'}. Additional events are non-payable and publisher postbacks can be fired manually.
                    </small>
                  </div>
                  <button type="button" className="btn btn-secondary btn-compact" onClick={addEventConfig}>
                    Add Event
                  </button>
                </div>

                {eventConfigs.length > 0 && (
                  <div className="event-config-list">
                    {eventConfigs.map((eventConfig, index) => (
                      <div className="event-config-card" key={`event-config-${index}`}>
                        <div className="event-config-card-header">
                          <strong>Event {index + 2}</strong>
                          <button type="button" className="btn btn-secondary btn-compact" onClick={() => removeEventConfig(index)}>
                            Remove
                          </button>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label>Event Name *</label>
                            <input
                              type="text"
                              value={eventConfig.eventName}
                              onChange={(e) => updateEventConfig(index, 'eventName', e.target.value)}
                              className={errors[`event_${index}`] ? 'error' : ''}
                              placeholder="paid_upgrade"
                            />
                            {errors[`event_${index}`] && <span className="error-message">{errors[`event_${index}`]}</span>}
                          </div>

                          <div className="form-group">
                            <label>Payout</label>
                            <input
                              type="number"
                              value={eventConfig.payout}
                              onChange={(e) => updateEventConfig(index, 'payout', e.target.value)}
                              placeholder={formData.payout || '0.00'}
                              step="0.01"
                              min="0"
                              disabled
                            />
                            <small className="field-hint">Disabled for non-payable supporting events</small>
                          </div>

                          <div className="form-group">
                            <label>Revenue</label>
                            <input
                              type="number"
                              value={eventConfig.revenue}
                              onChange={(e) => updateEventConfig(index, 'revenue', e.target.value)}
                              placeholder={eventConfig.payout || formData.payout || '0.00'}
                              step="0.01"
                              min="0"
                              disabled
                            />
                            <small className="field-hint">Disabled for non-payable supporting events</small>
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label>Daily Conversion Cap</label>
                            <input
                              type="number"
                              value={eventConfig.dailyConversionCap}
                              onChange={(e) => updateEventConfig(index, 'dailyConversionCap', e.target.value)}
                              placeholder="No limit"
                              min="0"
                            />
                          </div>

                          <div className="form-group">
                            <label>Monthly Conversion Cap</label>
                            <input
                              type="number"
                              value={eventConfig.monthlyConversionCap}
                              onChange={(e) => updateEventConfig(index, 'monthlyConversionCap', e.target.value)}
                              placeholder="No limit"
                              min="0"
                            />
                          </div>
                        </div>

                        <div className="event-config-toggles">
                          <span className="event-config-note">Non-payable. Auto publisher postback is disabled; use manual fire if needed.</span>
                          <label className="checkbox-item">
                            <input
                              type="checkbox"
                              checked={eventConfig.isActive}
                              onChange={(e) => updateEventConfig(index, 'isActive', e.target.checked)}
                            />
                            Active
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="dailyClickCap">Daily Click Cap</label>
                <input
                  type="number"
                  id="dailyClickCap"
                  name="dailyClickCap"
                  value={formData.dailyClickCap}
                  onChange={handleInputChange}
                  placeholder="0 for unlimited"
                  min="0"
                />
                <small className="field-hint">Maximum clicks per day (0 for unlimited)</small>
              </div>

              <div className="form-group">
                <label htmlFor="monthlyClickCap">Monthly Click Cap</label>
                <input
                  type="number"
                  id="monthlyClickCap"
                  name="monthlyClickCap"
                  value={formData.monthlyClickCap}
                  onChange={handleInputChange}
                  placeholder="0 for unlimited"
                  min="0"
                />
                <small className="field-hint">Maximum clicks per month (0 for unlimited)</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="dailyConversionCap">Daily Conversion Cap</label>
                <input
                  type="number"
                  id="dailyConversionCap"
                  name="dailyConversionCap"
                  value={formData.dailyConversionCap}
                  onChange={handleInputChange}
                  placeholder="0 for unlimited"
                  min="0"
                />
                <small className="field-hint">Maximum conversions per day (0 for unlimited)</small>
              </div>

              <div className="form-group">
                <label htmlFor="monthlyConversionCap">Monthly Conversion Cap</label>
                <input
                  type="number"
                  id="monthlyConversionCap"
                  name="monthlyConversionCap"
                  value={formData.monthlyConversionCap}
                  onChange={handleInputChange}
                  placeholder="0 for unlimited"
                  min="0"
                />
                <small className="field-hint">Maximum conversions per month (0 for unlimited)</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startDate">Start Date</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                />
                <small className="field-hint">Offer starts at midnight IST on this date (leave empty for no start date)</small>
              </div>

              <div className="form-group">
                <label htmlFor="endDate">End Date</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                />
                <small className="field-hint">Offer ends at 11:59 PM IST on this date (leave empty for no end date)</small>
              </div>
            </div>

          </div>

          <div className="form-section">
            <h2>URLs & Tracking</h2>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="redirectUrlTemplate">Redirect URL Template *</label>
                <input
                  type="text"
                  id="redirectUrlTemplate"
                  name="redirectUrlTemplate"
                  value={formData.redirectUrlTemplate}
                  onChange={handleInputChange}
                  className={errors.redirectUrlTemplate ? 'error' : ''}
                  placeholder="https://track.example.com/click?offer_id={offer_id}&aff_id={aff_id}"
                />
                {errors.redirectUrlTemplate && <span className="error-message">{errors.redirectUrlTemplate}</span>}
                <small className="field-hint">
                  Use macros: {'{click_id}'}, {'{offer_id}'}, {'{aff_id}'}, {'{aff_sub1}'} (click ID), {'{device_id}'}, {'{gaid}'} (Google ID), {'{ifaid}'} (Apple IDFA)
                </small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="previewUrl">Preview URL</label>
                <input
                  type="text"
                  id="previewUrl"
                  name="previewUrl"
                  value={formData.previewUrl}
                  onChange={handleInputChange}
                  placeholder="https://example.com/app-preview"
                />
                <small className="field-hint">URL where publishers can preview the offer</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter offer description"
                  rows={3}
                />
                <small className="field-hint">Detailed description of the offer for publishers</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="restriction">Restrictions</label>
                <textarea
                  id="restriction"
                  name="restriction"
                  value={formData.restriction}
                  onChange={handleInputChange}
                  placeholder="Enter any restrictions or requirements for this offer"
                  rows={3}
                />
                <small className="field-hint">Any restrictions, requirements, or special notes for publishers</small>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Targeting</h2>

            <div className="form-row">
              <div className="form-group" style={{ position: 'relative' }}>
                <label>Countries * (Multi-select)</label>
                <input
                  ref={countryInputRef}
                  type="text"
                  value={countrySearch}
                  onChange={handleCountrySearch}
                  onFocus={() => setShowCountryDropdown(true)}
                  className="form-input"
                  placeholder="Type to search and select countries..."
                  autoComplete="off"
                />

                {/* Selected Countries Display */}
                {formData.countries.length > 0 && (
                  <div className="selected-countries" style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {formData.countries.map(country => (
                      <span
                        key={country}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 8px',
                          backgroundColor: '#3498db',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '13px'
                        }}
                      >
                        {country}
                        <button
                          type="button"
                          onClick={() => handleRemoveCountry(country)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '16px',
                            padding: '0',
                            marginLeft: '2px'
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Country Dropdown */}
                {showCountryDropdown && filteredCountries.length > 0 && (
                  <div
                    ref={countryDropdownRef}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      maxHeight: '250px',
                      overflowY: 'auto',
                      backgroundColor: '#4a5568',
                      border: '1px solid #5a6c7d',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      zIndex: 1000,
                      marginTop: '4px'
                    }}
                  >
                    {/* Select All / Deselect All */}
                    <div
                      style={{
                        padding: '8px 12px',
                        borderBottom: '2px solid #5a6c7d',
                        backgroundColor: '#3c4855',
                        display: 'flex',
                        gap: '10px'
                      }}
                    >
                      <button
                        type="button"
                        onClick={handleSelectAllCountries}
                        style={{
                          flex: 1,
                          padding: '6px 12px',
                          backgroundColor: '#3498db',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={handleDeselectAllCountries}
                        style={{
                          flex: 1,
                          padding: '6px 12px',
                          backgroundColor: '#e74c3c',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        Deselect All
                      </button>
                    </div>
                    {filteredCountries.map((country) => (
                      <div
                        key={country}
                        onClick={() => handleCountryToggle(country)}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #5a6c7d',
                          color: '#ecf0f1',
                          backgroundColor: formData.countries.includes(country) ? '#3498db' : 'transparent',
                          transition: 'background-color 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                        onMouseEnter={(e) => {
                          if (!formData.countries.includes(country)) {
                            e.currentTarget.style.backgroundColor = '#3c4855';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!formData.countries.includes(country)) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.countries.includes(country)}
                          readOnly
                          style={{ marginRight: '4px' }}
                        />
                        {country}
                      </div>
                    ))}
                  </div>
                )}
                {errors.countries && <span className="error-message">{errors.countries}</span>}
                <small className="field-hint">Selected: {formData.countries.length} countries</small>
              </div>

              <div className="form-group">
                <label htmlFor="deviceTypes">Device Types *</label>
                <select
                  id="deviceTypes"
                  value={formData.deviceTypes.length > 0 ? formData.deviceTypes[0] : ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, deviceTypes: [e.target.value] }))}
                  className={errors.deviceTypes ? 'error' : ''}
                >
                  <option value="">Select Device Type</option>
                  {availableDeviceTypes.map(device => (
                    <option key={device} value={device}>
                      {device}
                    </option>
                  ))}
                </select>
                <small className="field-hint">Select primary device type</small>
                {errors.deviceTypes && <span className="error-message">{errors.deviceTypes}</span>}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Status</h2>

            <div className="form-row">
              <div className="form-group">
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  Active (offer can receive traffic)
                </label>
              </div>

              <div className="form-group">
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    name="isTest"
                    checked={formData.isTest}
                    onChange={handleInputChange}
                  />
                  Test Offer (for testing purposes)
                </label>
              </div>
            </div>
          </div>

          {/* Publisher Assignment Section */}
          <div className="form-section" style={{ marginTop: '30px', padding: '20px', backgroundColor: '#2d3748', borderRadius: '8px' }}>
            <h2 style={{ marginBottom: '10px', color: '#ecf0f1' }}>Assigned Publishers</h2>
            <p style={{ color: '#a0aec0', fontSize: '14px', marginBottom: '20px' }}>
              Manage which publishers can promote this offer
            </p>

            <div style={{ marginBottom: '15px' }}>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => setShowAssignModal(true)}
                disabled={getAvailablePublishers().length === 0}
              >
                + Assign Publisher
              </button>
            </div>

            {assignments.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#a0aec0', backgroundColor: '#4a5568', borderRadius: '4px' }}>
                No publishers assigned to this offer yet.
              </div>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#4a5568', position: 'sticky', top: 0 }}>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #2d3748' }}>Publisher</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #2d3748' }}>Status</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #2d3748' }}>Custom Payout</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #2d3748' }}>Assigned Date</th>
                      <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #2d3748' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignments.map((assignment: any) => (
                      <tr key={assignment.id} style={{ borderBottom: '1px solid #4a5568' }}>
                        <td style={{ padding: '10px' }}>
                          <div style={{ fontWeight: 'bold' }}>{assignment.publisher.name}</div>
                          <div style={{ fontSize: '12px', color: '#a0aec0' }}>{assignment.publisher.email}</div>
                        </td>
                        <td style={{ padding: '10px' }}>
                          <span className={`status-badge ${assignment.status}`}>
                            {assignment.status.toUpperCase()}
                          </span>
                        </td>
                        <td style={{ padding: '10px' }}>
                          {assignment.custom_payout ? (
                            <span style={{ fontWeight: 'bold', color: '#27ae60' }}>
                              ${assignment.custom_payout}
                            </span>
                          ) : (
                            <span style={{ color: '#a0aec0' }}>Default (${formData.payout})</span>
                          )}
                        </td>
                        <td style={{ padding: '10px', fontSize: '13px' }}>
                          {new Date(assignment.assigned_at).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => handleRemovePublisher(assignment.publisher.id)}
                            style={{ fontSize: '12px' }}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Update Offer
            </button>
          </div>
        </form>
      </div>

      {/* Assign Publisher Modal */}
      {showAssignModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#2d3748',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Assign Publishers to Offer</h2>

            <div style={{ marginBottom: '15px', position: 'relative' }} data-publisher-dropdown>
              <label style={{ display: 'block', marginBottom: '5px' }}>Search & Select Publishers</label>
              <input
                type="text"
                value={publisherSearch}
                onChange={(e) => setPublisherSearch(e.target.value)}
                onFocus={() => setShowPublisherDropdown(true)}
                placeholder="Type to search publishers..."
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: '#4a5568',
                  color: '#ecf0f1',
                  border: '1px solid #718096',
                  borderRadius: '4px'
                }}
              />

              {/* Selected Publishers Display */}
              {selectedPublisherIds.length > 0 && (
                <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {selectedPublisherIds.map(publisherId => {
                    const publisher = allPublishers.find(p => p.id === publisherId);
                    return publisher ? (
                      <span
                        key={publisherId}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 8px',
                          backgroundColor: '#3498db',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '13px'
                        }}
                      >
                        {publisher.name}
                        <button
                          type="button"
                          onClick={() => handleRemoveSelectedPublisher(publisherId)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '16px',
                            padding: '0',
                            marginLeft: '2px'
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              )}

              {/* Publisher Dropdown */}
              {showPublisherDropdown && (
                <div
                  ref={publisherDropdownRef}
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    backgroundColor: '#4a5568',
                    border: '1px solid #5a6c7d',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    zIndex: 1001,
                    marginTop: '4px'
                  }}
                >
                  {/* Select All / Deselect All */}
                  <div
                    style={{
                      padding: '8px 12px',
                      borderBottom: '2px solid #5a6c7d',
                      backgroundColor: '#3c4855',
                      display: 'flex',
                      gap: '10px',
                      position: 'sticky',
                      top: 0
                    }}
                  >
                    <button
                      type="button"
                      onClick={handleSelectAllPublishers}
                      style={{
                        flex: 1,
                        padding: '6px 12px',
                        backgroundColor: '#3498db',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={handleDeselectAllPublishers}
                      style={{
                        flex: 1,
                        padding: '6px 12px',
                        backgroundColor: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Deselect All
                    </button>
                  </div>
                  {getAvailablePublishers()
                    .filter(pub =>
                      pub.name.toLowerCase().includes(publisherSearch.toLowerCase()) ||
                      pub.email.toLowerCase().includes(publisherSearch.toLowerCase())
                    )
                    .map((publisher) => (
                      <div
                        key={publisher.id}
                        onClick={() => handlePublisherToggle(publisher.id)}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #5a6c7d',
                          color: '#ecf0f1',
                          backgroundColor: selectedPublisherIds.includes(publisher.id) ? '#3498db' : 'transparent',
                          transition: 'background-color 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                        onMouseEnter={(e) => {
                          if (!selectedPublisherIds.includes(publisher.id)) {
                            e.currentTarget.style.backgroundColor = '#3c4855';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!selectedPublisherIds.includes(publisher.id)) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedPublisherIds.includes(publisher.id)}
                          readOnly
                          style={{ marginRight: '4px' }}
                        />
                        <div>
                          <div style={{ fontWeight: 'bold' }}>{publisher.name}</div>
                          <div style={{ fontSize: '11px', color: '#a0aec0' }}>{publisher.email}</div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
              <small style={{ color: '#a0aec0', fontSize: '11px' }}>Selected: {selectedPublisherIds.length} publisher(s)</small>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>
                Custom Payout (optional, default: ${formData.payout})
              </label>
              <input
                type="number"
                step="0.01"
                value={customPayout}
                onChange={(e) => setCustomPayout(e.target.value)}
                placeholder={`Default: ${formData.payout}`}
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: '#4a5568',
                  color: '#ecf0f1',
                  border: '1px solid #718096',
                  borderRadius: '4px'
                }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#ecf0f1' }}>
                Per-Publisher Caps (Optional)
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px' }}>Daily Click Cap</label>
                  <input
                    type="number"
                    value={pubDailyClickCap}
                    onChange={(e) => setPubDailyClickCap(e.target.value)}
                    placeholder="0 for unlimited"
                    style={{
                      width: '100%',
                      padding: '8px',
                      backgroundColor: '#4a5568',
                      color: '#ecf0f1',
                      border: '1px solid #718096',
                      borderRadius: '4px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px' }}>Monthly Click Cap</label>
                  <input
                    type="number"
                    value={pubMonthlyClickCap}
                    onChange={(e) => setPubMonthlyClickCap(e.target.value)}
                    placeholder="0 for unlimited"
                    style={{
                      width: '100%',
                      padding: '8px',
                      backgroundColor: '#4a5568',
                      color: '#ecf0f1',
                      border: '1px solid #718096',
                      borderRadius: '4px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px' }}>Daily Conversion Cap</label>
                  <input
                    type="number"
                    value={pubDailyConversionCap}
                    onChange={(e) => setPubDailyConversionCap(e.target.value)}
                    placeholder="0 for unlimited"
                    style={{
                      width: '100%',
                      padding: '8px',
                      backgroundColor: '#4a5568',
                      color: '#ecf0f1',
                      border: '1px solid #718096',
                      borderRadius: '4px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px' }}>Monthly Conversion Cap</label>
                  <input
                    type="number"
                    value={pubMonthlyConversionCap}
                    onChange={(e) => setPubMonthlyConversionCap(e.target.value)}
                    placeholder="0 for unlimited"
                    style={{
                      width: '100%',
                      padding: '8px',
                      backgroundColor: '#4a5568',
                      color: '#ecf0f1',
                      border: '1px solid #718096',
                      borderRadius: '4px'
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Notes (optional)</label>
              <textarea
                value={assignmentNotes}
                onChange={(e) => setAssignmentNotes(e.target.value)}
                placeholder="Internal notes about this assignment..."
                rows={2}
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: '#4a5568',
                  color: '#ecf0f1',
                  border: '1px solid #718096',
                  borderRadius: '4px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedPublisherIds([]);
                  setPublisherSearch('');
                  setShowPublisherDropdown(false);
                  setCustomPayout('');
                  setAssignmentNotes('');
                  setPubDailyClickCap('');
                  setPubMonthlyClickCap('');
                  setPubDailyConversionCap('');
                  setPubMonthlyConversionCap('');
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAssignPublisher}
                disabled={selectedPublisherIds.length === 0}
              >
                Assign {selectedPublisherIds.length > 0 ? `${selectedPublisherIds.length} ` : ''}Publisher{selectedPublisherIds.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditOffer;
